<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue';
import { DsIconButton, dsUseUniqueId } from '@coloplast/design-system';
import { youtubeVideoUrl } from 'shared/services/urlTypeService';
import { track_content_engagement } from 'shared/services/tracking/contentTrackingService';
import Events from 'shared/settings/events';

const props = defineProps({
	videoUrl: {
		type: String,
		required: false,
	},
	videoWrapperClass: {
		type: String,
		required: false,
		default: () => '--loop'
	},
	headline: {
		type: String,
		default: '',
	},
	playOnLoad: {
		type: Boolean,
		default: true,
	},
	videoId: {
		type: String,
	},
	aspectRatio: {
		type: String,
		default: 'auto',
	},
	playAriaLabel: String,
	pauseAriaLabel: String,
	videoTitle: String,
});

const uniqueId = dsUseUniqueId('split-media-block-video-loop');

// Devise the "id" that need to be supplied to the youtube and vimeo player apis
let videoId;
if (props.videoUrl) {
	if (/watch\?v=([^&$]*)/.test(props.videoUrl)) {
		const m = /watch\?v=([^&$]*)/.exec(props.videoUrl);
		if (m) {
			videoId = ref(m[1]);
		}
	}
	else {
		const urlParts = props.videoUrl.split('/');
		videoId = ref(urlParts[urlParts.length - 1]);
	}
}

let videoPlayer;

const
	video = ref(null),
	videoIsPlaying = ref(false);

const
	vimeoVideoParams = computed(() => {
		let params = `?autoplay=${props.playOnLoad ? '1' : '0'}&muted=1&controls=0&api=1&title=0&byline=0&portrait=0&sidedock=0&playsinline=1&autopip=none&pip=none&byline=0&responsive=1&loop=1`;
		if (!window.marketingPermissions) {
			params += '&dnt=true';
		}
		return params;
	}),
	isVimeo = computed(() => props.videoUrl.indexOf('vimeo.com') != -1),
	embedUrl = computed(() => {
		const
			playOnLoad = props.playOnLoad ? '1' : '0';
		const
			embedTemplate = isVimeo.value ? 'https://player.vimeo.com/video/' : youtubeVideoUrl(),
			embedParams = isVimeo.value ? vimeoVideoParams.value : `?fs=0&modestbranding=0&cc_load_policy=0&controls=0&iv_load_policy=3&enablejsapi=1&version=3&playsinline=1&autoplay=${playOnLoad}&mute=1&playlist=${videoId.value}&loop=1`;
		if (videoId.value) {
			return `${embedTemplate}${videoId.value}${embedParams}`;
		}
		else
			return null;
	});

function loadScript() {
	let videoApi = document.createElement('script');
	videoApi.id = isVimeo.value ? 'iframe-vimeo' : 'iframe-youtube';
	videoApi.src = isVimeo.value ? 'https://player.vimeo.com/api/player.js' : 'https://www.youtube.com/iframe_api';
	videoApi.type = 'text/javascript';
	videoApi.async = true;

	const firstScriptTag = document.getElementsByTagName('script')[0];
	firstScriptTag.parentNode.insertBefore(videoApi, firstScriptTag);

	videoApi.onload = () => {
		nextTick(() => {
			if (isVimeo.value) {
				videoPlayer = new Vimeo.Player(video.value);
				videoPlayer.on('loaded', () => {
					videoIsPlaying.value = props.playOnLoad;
				});
			}
			else {
				window.onYouTubePlayerAPIReady = () => {
					videoPlayer = new YT.Player(video.value, {
						events: {
							'onReady': () => {
								videoIsPlaying.value = props.playOnLoad;
							},
						}
					});
				}
			}
		});
	};
}
loadScript();

const videoTracked = ref(false);

watch(videoIsPlaying, (newValue, oldValue) => {
	if (oldValue === false && newValue === true) {
		if (!videoTracked.value) {
			track_content_engagement('video', 'start', props.headline || 'no_data', props.videoUrl);
			videoTracked.value = true;
		}
		const event = new CustomEvent(Events.VIDEO_PLAYING, {
		detail: {
				id: props.videoId,
			},
		});
		document.dispatchEvent(event);
	}
});

onMounted(() => {
	// if a video id has been supplied set up an event listener that listens for play events to play/pause the video
	if (props.videoId) {
		document.addEventListener(Events.PLAY, (event) => {
			if (event.detail.id == props.videoId) {
				playVideo();
			}
		});
		document.addEventListener(Events.PAUSE, (event) => {
			if (event.detail.id == props.videoId) {
				pauseVideo();
			}
		});
	}
});

function playVideo() {
	videoIsPlaying.value = true;
	if (!isVimeo.value) {
		videoPlayer.playVideo();
	}
	else {
		videoPlayer.play();
	}

	track_content_engagement('video', 'start', props.headline || 'no_data', props.videoUrl);
}

function pauseVideo() {
	videoIsPlaying.value = false;
	if (!isVimeo.value) {
		videoPlayer.pauseVideo();
	}
	else {
		videoPlayer.pause();
	}
}
</script>

<template>
	<div v-if="embedUrl"
		class="c-video-item"
		:class="videoWrapperClass"
		:style="'--video-loop-aspect-ratio: ' + props.aspectRatio"
	>
		<iframe ref="video"
			:id="uniqueId"
				class="c-video-item__iframe"
				:title="videoTitle"
				:data-src="embedUrl"
				:allow="[ isVimeo ? 'autoplay' : 'autoplay; accelerometer; encrypted-media; gyroscope' ]"
				:src="embedUrl">
		</iframe>

		<DsIconButton v-if="!videoIsPlaying"
						:aria-describedby="uniqueId"
						:ariaLabel="playAriaLabel"
						icon-name="play"
						variant="secondary"
						class="c-video-item__videocta"
						:clickHandler="() => playVideo()" />
		<DsIconButton v-else
						:aria-describedby="uniqueId"
						:ariaLabel="pauseAriaLabel"
						icon-name="pause"
						variant="secondary"
						class="c-video-item__videocta"
						:clickHandler="() => pauseVideo()" />
	</div>
</template>
